<template>
  <div v-if="show" class="modal-overlay" @click.self="handleCancel">
    <div class="modal-content bulgarian-invoice-modal">
      <div class="modal-header">
        <div class="header-icon">
          <LineIcon name="fuel-pump" :size="15" :stroke-width="1.8" class="icon-gas-pump" />
        </div>
        <div class="header-content">
          <h3>{{ t('bulgarianInvoice.title') }}</h3>
          <p class="modal-subtitle">
            {{ t(invoiceCount === 1 ? 'bulgarianInvoice.subtitleSimpleSingle' : 'bulgarianInvoice.subtitleSimple', { count: invoiceCount }) }}
          </p>
        </div>
        <button @click="handleCancel" class="btn btn-icon">
          <LineIcon name="x" :size="15" :stroke-width="1.8" />
        </button>
      </div>
      
      <div class="modal-body">
        <div class="info-section">
          <div class="info-icon">
            <LineIcon name="info" :size="15" :stroke-width="1.8" />
          </div>
          <p>
            {{ t('bulgarianInvoice.infoText') }}
          </p>
        </div>

        <div class="form-group">
          <label for="bulgarian-role-select">
            <LineIcon name="building" :size="15" :stroke-width="1.8" />
            {{ t('bulgarianInvoice.myCompanyRole') }}
          </label>
          <SelectField v-model="selectedRole" :options="roleOptions" class="form-select" />
          <p class="help-text">
            <strong>{{ t('bulgarianInvoice.buyerHelp').split(':')[0] }}:</strong> {{ t('bulgarianInvoice.buyerHelp').split(':')[1] }}<br>
            <strong>{{ t('bulgarianInvoice.sellerHelp').split(':')[0] }}:</strong> {{ t('bulgarianInvoice.sellerHelp').split(':')[1] }}
          </p>
        </div>

        <div class="form-group">
          <label for="bulgarian-payment-select">
            <LineIcon name="credit-card" :size="15" :stroke-width="1.8" />
            {{ t('bulgarianInvoice.paymentMethod') }}
          </label>
          <SelectField v-model="selectedPaymentMethod" :options="selectedPaymentMethodOptions" class="form-select" id="bulgarian-payment-select" />
        </div>

        <div class="summary-box">
          <div class="summary-title">
            <LineIcon name="file-text" :size="15" :stroke-width="1.8" />
            {{ t('bulgarianInvoice.summary') }}
          </div>
          <div class="summary-content">
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalCompanies') }}</span>
              <strong>{{ companyCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalInvoices') }}</span>
              <strong>{{ invoiceCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalItems') }}</span>
              <strong>{{ itemCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.grandTotal') }}</span>
              <strong>{{ grandTotal }} BGN</strong>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="handleCancel" class="btn-cancel">
          <LineIcon name="x" :size="15" :stroke-width="1.8" />
          {{ t('bulgarianInvoice.cancel') }}
        </button>
        <button @click="handleConfirm" class="btn-confirm" :disabled="processing">
          <LineIcon name="check" :size="15" :stroke-width="1.8" v-if="!processing" />
          <div v-else class="loading-spinner-small"></div>
          {{ processing ? t('bulgarianInvoice.processing') : t('bulgarianInvoice.confirmProcess') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import SelectField from '@/components/SelectField.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  bulgarianData: {
    type: Object,
    default: null
  },
  processing: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['confirm', 'cancel']);

const selectedRole = ref('buyer');
const selectedPaymentMethod = ref('Bank Transfer');

const invoiceCount = computed(() => {
  return props.bulgarianData?.summary?.total_invoices || 0;
});

const companyCount = computed(() => {
  return props.bulgarianData?.summary?.total_companies || 0;
});

const itemCount = computed(() => {
  return props.bulgarianData?.summary?.total_items || 0;
});

const grandTotal = computed(() => {
  return props.bulgarianData?.summary?.grand_total?.toFixed(2) || '0.00';
});

const handleConfirm = () => {
  emit('confirm', {
    role: selectedRole.value,
    paymentMethod: selectedPaymentMethod.value
  });
};

const handleCancel = () => {
  emit('cancel');
};

/* What each field offers, as a list: the fields are the app's own
   dropdowns now, so the options are data rather than tags. */
const selectedPaymentMethodOptions = computed(() => [
  { value: 'Bank Transfer', label: t('bulgarianInvoice.paymentBankTransfer') },
  { value: 'Cash', label: t('bulgarianInvoice.paymentCash') },
  { value: 'Credit Card', label: t('bulgarianInvoice.paymentCreditCard') },
  { value: 'Check', label: t('bulgarianInvoice.paymentCheck') },
  { value: 'Online Payment', label: t('bulgarianInvoice.paymentOnline') },
  { value: 'Deferred Payment', label: t('bulgarianInvoice.paymentDeferred') },
  { value: 'Partial Payment', label: t('bulgarianInvoice.paymentPartial') },
  { value: 'Other', label: t('bulgarianInvoice.paymentOther') },
]);

/* Which side of the invoice this company is on. The marks the <option> tags
   carried were never drawn — a browser's list renders text and nothing else. */
const roleOptions = computed(() => [
  { value: 'buyer', label: t('bulgarianInvoice.buyerOption') },
  { value: 'seller', label: t('bulgarianInvoice.sellerOption') },
]);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
  padding: 20px;
  overflow-y: auto;
}

.bulgarian-invoice-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: scaleIn 0.3s ease;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid var(--border);
  position: relative;
}

.header-icon {
  background: var(--primary);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.icon-gas-pump {
  /* Optional: remove animation for cleaner look */
}

.header-content {
  flex: 1;
}

.header-content h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-section {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border-left: 4px solid var(--primary);
  border-radius: 8px;
}

.info-icon {
  color: var(--primary);
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-section p {
  margin: 0;
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.6;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group label svg {
  font-size: 16px;
  color: var(--primary);
}

.form-select {
  padding: 12px 14px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-select:hover {
  border-color: var(--primary);
}

.help-text {
  margin: 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.help-text strong {
  color: var(--text-primary);
}

.summary-box {
  background: white;
  border: 2px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.summary-title {
  background: var(--primary);
  color: white;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.summary-row span {
  color: var(--text-secondary);
}

.summary-row strong {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border);
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-cancel {
  background: white;
  color: var(--text-primary);
  border: 2px solid var(--border);
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: var(--text-secondary);
}

.btn-confirm {
  background: var(--primary);
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
